import { useQuery } from 'react-query';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/Card';
import { clientPortalAPI } from '../../api';
import { Award, Download, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const certTypeLabel = (type) => {
  const t = (type || '').toLowerCase();
  if (t === 'eicr') return 'EICR';
  if (t === 'eic') return 'EIC';
  if (t === 'mewc') return 'MEWC';
  return type || '—';
};

export default function ClientCertificates() {
  const { data: certificates = [], isLoading, error } = useQuery({
    queryKey: ['client-portal', 'my-certificates'],
    queryFn: () => clientPortalAPI.getMyCertificates(),
  });

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
            Your Certificates
          </h1>
          <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
            View and download your electrical certificates
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Card className="bg-white dark:bg-gray-800">
          <CardContent className="p-0">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[var(--ds-text-muted)]" />
              </div>
            )}
            {error && (
              <div className="p-6 text-center text-red-600 dark:text-red-400">
                Failed to load certificates. Please try again later.
              </div>
            )}
            {!isLoading && !error && certificates.length === 0 && (
              <div className="p-12 text-center text-[var(--ds-text-secondary)]">
                <Award className="mx-auto h-12 w-12 text-[var(--ds-text-muted)] mb-3" />
                <p className="text-body">You have no certificates yet.</p>
              </div>
            )}
            {!isLoading && !error && certificates.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Certificate
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Type
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Job reference
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Date issued
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Status
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300 text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {certificates.map((cert) => (
                      <tr
                        key={cert.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/30"
                      >
                        <td className="px-4 py-3 text-body font-medium text-[var(--ds-text-primary)]">
                          {cert.certificateNum || '—'}
                        </td>
                        <td className="px-4 py-3 text-body text-[var(--ds-text-secondary)]">
                          {certTypeLabel(cert.certificateType)}
                        </td>
                        <td className="px-4 py-3 text-body text-[var(--ds-text-secondary)]">
                          {cert.jobReference || '—'}
                        </td>
                        <td className="px-4 py-3 text-body text-[var(--ds-text-secondary)]">
                          {cert.dateIssued
                            ? format(new Date(cert.dateIssued), 'dd MMM yyyy')
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-body text-[var(--ds-text-secondary)]">
                          {cert.status || '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <a
                            href={`${baseUrl}/api/portal/certificate/${cert.viewToken}/pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            <Download className="h-4 w-4" />
                            Download PDF
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
